export interface CrosswordWordCell {
  row: number;
  col: number;
  char: string;
  isSpace?: boolean;
}

export interface CrosswordWord {
  id: string;
  number: number;
  direction: 'across' | 'down';
  row: number; // 0-indexed start row
  col: number; // 0-indexed start col
  kab: string; // uppercase normalized Kabyle word or phrase
  originalKab: string; // display Kabyle with diacritics
  fr: string; // French clue / definition
  category: string;
  icon?: string;
  culturalNote: string;
  phonetic?: string;
  step?: number;
  attachedToWordId?: string;
  attachType?: 'root' | 'intersect' | 'below';
  isPhrase?: boolean;
  cells?: CrosswordWordCell[];
}

export interface CrosswordGrid {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  difficulty: 'Initié' | 'Intermédiaire' | 'Maître';
  region: string;
  rows: number;
  cols: number;
  xpReward: number;
  requiredLevel: number;
  description: string;
  words: CrosswordWord[];
  isEvolving?: boolean;
}

export const CROSSWORD_GRIDS: CrosswordGrid[] = [
  // =========================================================================
  // GRILLE 1: TADDART (7x7) - Le Foyer & Les Bêtes
  // =========================================================================
  {
    id: 'grid_taddart_1',
    title: 'Le Foyer & Les Bêtes',
    subtitle: 'Taddart • Village Ancestral',
    icon: '🏡',
    difficulty: 'Initié',
    region: 'Taddart',
    rows: 7,
    cols: 7,
    xpReward: 1000,
    requiredLevel: 0,
    description: 'Explorez la maison kabyle, ses animaux protecteurs et les éléments fondateurs du village.',
    words: [
      {
        id: 'w1_1',
        number: 1,
        direction: 'across',
        row: 1,
        col: 1,
        kab: 'AXXAM',
        originalKab: 'Axxam',
        fr: 'Maison traditionnelle kabyle et sanctuaire de la famille',
        category: 'La Maison',
        icon: '🏠',
        culturalNote: 'L’axxam est le cœur de la société kabyle, bâti en pierres sèches avec sa charpente en bois de chêne ou d’olivier.',
        phonetic: 'akh-kham'
      },
      {
        id: 'w1_2',
        number: 2,
        direction: 'down',
        row: 1,
        col: 1,
        kab: 'AYDI',
        originalKab: 'Aydi',
        fr: 'Le chien fidèle, gardien du troupeau',
        category: 'Animaux',
        icon: '🐕',
        culturalNote: 'Les chiens de berger kabyles veillent sans relâche sur le troupeau contre les prédateurs des collines.',
        phonetic: 'ay-di'
      },
      {
        id: 'w1_3',
        number: 3,
        direction: 'down',
        row: 0,
        col: 5,
        kab: 'AMCIC',
        originalKab: 'Amcic',
        fr: 'Le chat de la maison',
        category: 'Animaux',
        icon: '🐈',
        culturalNote: 'Présent près du lkanun (foyer), il protège les réserves de grains et d’olives.',
        phonetic: 'am-chich'
      },
      {
        id: 'w1_4',
        number: 4,
        direction: 'across',
        row: 4,
        col: 1,
        kab: 'ITRI',
        originalKab: 'Itri',
        fr: 'Étoile qui guide les bergers la nuit',
        category: 'Nature & Météo',
        icon: '⭐',
        culturalNote: 'Les anciens s’orientaient grâce aux constellations au-dessus des crêtes du Djurdjura.',
        phonetic: 'it-ri'
      },
      {
        id: 'w1_5',
        number: 5,
        direction: 'down',
        row: 2,
        col: 3,
        kab: 'ADRAR',
        originalKab: 'Adrar',
        fr: 'La montagne majestueuse',
        category: 'Lieux & Espace',
        icon: '⛰️',
        culturalNote: 'Adrar n Djurdjura est le symbole de résistance, de dignité et de fierté de la Kabylie.',
        phonetic: 'adh-rar'
      },
      {
        id: 'w1_6',
        number: 6,
        direction: 'across',
        row: 6,
        col: 1,
        kab: 'ABRID',
        originalKab: 'Abrid',
        fr: 'Le chemin qui mène au village',
        category: 'Lieux & Espace',
        icon: '🛤️',
        culturalNote: 'Abrid désigne à la fois le sentier de montagne et la voie de la sagesse.',
        phonetic: 'ab-rid'
      }
    ]
  },

  // =========================================================================
  // GRILLE 2: AZEMMUR (8x8) - Les Vergers & Les Sources
  // =========================================================================
  {
    id: 'grid_azemmur_2',
    title: 'Les Vergers & Les Sources',
    subtitle: 'Azemmur • La Vallée Fertile',
    icon: '🫒',
    difficulty: 'Intermédiaire',
    region: 'Azemmur',
    rows: 8,
    cols: 8,
    xpReward: 1000,
    requiredLevel: 1,
    description: 'Découvrez les mots des récoltes d’olives, des cours d’eau vivifiants et de la vie rurale.',
    words: [
      {
        id: 'w2_1',
        number: 1,
        direction: 'across',
        row: 1,
        col: 0,
        kab: 'AZEMMUR',
        originalKab: 'Azemmur',
        fr: 'L’olivier ou les olives sacrées',
        category: 'Nature & Météo',
        icon: '🫒',
        culturalNote: 'Arbre béni de Kabylie, l’olivier fournit l’huile (Zit n uzemmur), base de l’alimentation et des remèdes traditionnels.',
        phonetic: 'a-zem-mour'
      },
      {
        id: 'w2_2',
        number: 2,
        direction: 'down',
        row: 0,
        col: 3,
        kab: 'AMAN',
        originalKab: 'Aman',
        fr: 'L’eau pure de la source du village',
        category: 'Nature & Météo',
        icon: '💧',
        culturalNote: '« Aman d tudert » (L’eau c’est la vie) : les sources alimentent la fontaine du village (Tala).',
        phonetic: 'a-man'
      },
      {
        id: 'w2_3',
        number: 3,
        direction: 'across',
        row: 3,
        col: 2,
        kab: 'ANZAR',
        originalKab: 'Anzar',
        fr: 'La pluie bienfaisante / le rite traditionnel de la fertilité',
        category: 'Nature & Météo',
        icon: '🌧️',
        culturalNote: 'Anzar est célébré dans les traditions anciennes pour appeler la pluie lors des saisons sèches.',
        phonetic: 'an-zar'
      },
      {
        id: 'w2_4',
        number: 4,
        direction: 'down',
        row: 3,
        col: 5,
        kab: 'ASIF',
        originalKab: 'Asif',
        fr: 'Rivière ou cours d’eau qui traverse la vallée',
        category: 'Nature & Météo',
        icon: '🌊',
        culturalNote: 'Les rivières comme l’Asif n Sahel irriguent les vergers et cultures maraîchères.',
        phonetic: 'a-sif'
      },
      {
        id: 'w2_5',
        number: 5,
        direction: 'across',
        row: 5,
        col: 0,
        kab: 'AYEFKI',
        originalKab: 'Ayefki',
        fr: 'Le lait frais de la ferme',
        category: 'Nourriture & Cuisine',
        icon: '🥛',
        culturalNote: 'Le lait frais sert à la confection du beurre et accompagne les galettes d’orge (aɣrum).',
        phonetic: 'ay-ef-ki'
      }
    ]
  },

  // =========================================================================
  // GRILLE 3: DJURDJURA (7x7) - Le Cœur & Les Saisons
  // =========================================================================
  {
    id: 'grid_djurdjura_3',
    title: 'Le Cœur & Les Cimes',
    subtitle: 'Djurdjura • Les Hautes Terres',
    icon: '❤️',
    difficulty: 'Intermédiaire',
    region: 'Djurdjura',
    rows: 7,
    cols: 7,
    xpReward: 1000,
    requiredLevel: 2,
    description: 'Explorez le vocabulaire des sentiments profonds, des cimes enneigées et de la lumière.',
    words: [
      {
        id: 'w3_1',
        number: 1,
        direction: 'across',
        row: 1,
        col: 1,
        kab: 'TASA',
        originalKab: 'Tasa',
        fr: 'Le foie / Le siège de l’amour maternel et des émotions',
        category: 'Le Corps & Soins',
        icon: '❤️',
        culturalNote: 'En kabyle, « Tasa-w » (Mon foie) est le terme affectueux le plus fort pour désigner son enfant chéri.',
        phonetic: 'ta-sa'
      },
      {
        id: 'w3_2',
        number: 2,
        direction: 'down',
        row: 1,
        col: 1,
        kab: 'TAFAT',
        originalKab: 'Tafat',
        fr: 'La lumière éclatante du matin',
        category: 'Nature & Météo',
        icon: '☀️',
        culturalNote: 'Tafat symbolise aussi la clarté d’esprit et l’espoir dans la poésie kabyle.',
        phonetic: 'ta-fat'
      },
      {
        id: 'w3_3',
        number: 3,
        direction: 'down',
        row: 1,
        col: 3,
        kab: 'SEMMID',
        originalKab: 'Semmiḍ',
        fr: 'Le grand froid des hauteurs',
        category: 'Nature & Météo',
        icon: '❄️',
        culturalNote: 'Le froid mordant de l’hiver kabyle rassemble toute la famille autour du foyer lkanun.',
        phonetic: 'sem-midh'
      },
      {
        id: 'w3_4',
        number: 4,
        direction: 'across',
        row: 4,
        col: 1,
        kab: 'ASMAD',
        originalKab: 'Asmaḍ',
        fr: 'L’air glacial ou le vent des sommets',
        category: 'Nature & Météo',
        icon: '💨',
        culturalNote: 'Le vent qui descend des crêtes du Djurdjura à la tombée de la nuit.',
        phonetic: 'as-madh'
      },
      {
        id: 'w3_5',
        number: 5,
        direction: 'down',
        row: 3,
        col: 5,
        kab: 'DDER',
        originalKab: 'Dder',
        fr: 'Vivre, exister et persévérer',
        category: 'Actions & Verbes',
        icon: '🌱',
        culturalNote: 'Un verbe fondateur exprimant la vitalité et la résilience.',
        phonetic: 'd-der'
      }
    ]
  },

  // =========================================================================
  // GRILLE 4: TAFERKA (8x8) - Savoirs & Artisanat
  // =========================================================================
  {
    id: 'grid_artisanat_4',
    title: 'Objets & Coutumes du Foyer',
    subtitle: 'Taferka • Savoirs & Métiers',
    icon: '🏺',
    difficulty: 'Maître',
    region: 'Taferka',
    rows: 8,
    cols: 8,
    xpReward: 1000,
    requiredLevel: 3,
    description: 'Pénétrez dans les ateliers des maîtres artisans : poteries, portes sculptées et sagesse.',
    words: [
      {
        id: 'w4_1',
        number: 1,
        direction: 'across',
        row: 1,
        col: 0,
        kab: 'TAWWURT',
        originalKab: 'Tawwurt',
        fr: 'La grande porte en bois sculpté de l’axxam',
        category: 'La Maison',
        icon: '🚪',
        culturalNote: 'Les portes kabyles sont ornées de motifs géométriques berbères protégeant le foyer du mauvais œil.',
        phonetic: 'taw-wourt'
      },
      {
        id: 'w4_2',
        number: 2,
        direction: 'down',
        row: 1,
        col: 1,
        kab: 'AXXAM',
        originalKab: 'Axxam',
        fr: 'La maison traditionnelle et la demeure familiale',
        category: 'La Maison',
        icon: '🏡',
        culturalNote: 'Bâtie de pierres taillées, elle abrite plusieurs générations sous le même toit.',
        phonetic: 'akh-kham'
      },
      {
        id: 'w4_3',
        number: 3,
        direction: 'down',
        row: 1,
        col: 6,
        kab: 'TAMURT',
        originalKab: 'Tamurt',
        fr: 'La terre natale, la patrie kabyle',
        category: 'Lieux & Espace',
        icon: '🗺️',
        culturalNote: '« Tamurt n Imaziɣen » : la terre des hommes libres.',
        phonetic: 'ta-mourt'
      },
      {
        id: 'w4_4',
        number: 4,
        direction: 'across',
        row: 4,
        col: 1,
        kab: 'AZEMMUR',
        originalKab: 'Azemmur',
        fr: 'L’arbre d’olivier sacré et vénéré',
        category: 'Nature & Météo',
        icon: '🫒',
        culturalNote: 'L’arbre d’abondance garantissant la prospérité du village pendant des siècles.',
        phonetic: 'a-zem-mour'
      },
      {
        id: 'w4_5',
        number: 5,
        direction: 'down',
        row: 3,
        col: 4,
        kab: 'IMAN',
        originalKab: 'Iman',
        fr: 'L’âme, l’esprit et la conscience',
        category: 'Culture & Société',
        icon: '🕊️',
        culturalNote: '« S yiman » : de toute son âme et avec toute sa sincérité.',
        phonetic: 'i-man'
      }
    ]
  },

  // =========================================================================
  // GRILLE 5: SANCTUAIRE (8x8) - Défi Céleste & Symboles
  // =========================================================================
  {
    id: 'grid_celeste_5',
    title: 'Le Sanctuaire Céleste & Symboles',
    subtitle: 'Itran • Défi des Érudits',
    icon: '✨',
    difficulty: 'Maître',
    region: 'Sanctuaire',
    rows: 8,
    cols: 8,
    xpReward: 1000,
    requiredLevel: 4,
    description: 'La plus haute épreuve : la lune, les assemblées de sages et les étoiles du Djurdjura.',
    words: [
      {
        id: 'w5_1',
        number: 1,
        direction: 'across',
        row: 1,
        col: 1,
        kab: 'AYYUR',
        originalKab: 'Ayyur',
        fr: 'La Lune qui éclaire le ciel nocturne',
        category: 'Nature & Météo',
        icon: '🌙',
        culturalNote: 'Ayyur rythme le calendrier agraire berbère (Yennayer) et les nuits de contes.',
        phonetic: 'ay-your'
      },
      {
        id: 'w5_2',
        number: 2,
        direction: 'down',
        row: 1,
        col: 1,
        kab: 'AGRAW',
        originalKab: 'Agraw',
        fr: 'L’assemblée des sages ou le rassemblement',
        category: 'Culture & Société',
        icon: '👥',
        culturalNote: 'Base démocratique villageoise où chaque citoyen prend la parole pour décider de la vie commune.',
        phonetic: 'ag-raw'
      },
      {
        id: 'w5_3',
        number: 3,
        direction: 'down',
        row: 1,
        col: 4,
        kab: 'UGUR',
        originalKab: 'Ugur',
        fr: 'L’obstacle ou le défi à surmonter',
        category: 'Actions & Verbes',
        icon: '🛡️',
        culturalNote: 'Rien n’arrête le courage de celui qui persévère dans l’apprentissage.',
        phonetic: 'ou-gour'
      },
      {
        id: 'w5_4',
        number: 4,
        direction: 'across',
        row: 4,
        col: 0,
        kab: 'TAZERT',
        originalKab: 'Tazert',
        fr: 'La figue sèche, aliment énergétique des montagnards',
        category: 'Nourriture & Cuisine',
        icon: '🌾',
        culturalNote: 'Conservée avec l’huile d’olive dans des jarres en terre cuite pour l’hiver.',
        phonetic: 'ta-zert'
      },
      {
        id: 'w5_5',
        number: 5,
        direction: 'down',
        row: 4,
        col: 5,
        kab: 'TALA',
        originalKab: 'Tala',
        fr: 'La fontaine ou la source d’eau sacrée',
        category: 'Lieux & Espace',
        icon: '⛲',
        culturalNote: 'Lieu de vie social essentiel où s’échangent les nouvelles et les poèmes du village.',
        phonetic: 'ta-la'
      },
      {
        id: 'w5_6',
        number: 6,
        direction: 'across',
        row: 7,
        col: 2,
        kab: 'ITRAN',
        originalKab: 'Itran',
        fr: 'Les étoiles au firmament',
        category: 'Nature & Météo',
        icon: '✨',
        culturalNote: 'Pluriel de « Itri », les étoiles ornent aussi les tatouages traditionnels sur les fronts des aïeules.',
        phonetic: 'it-ran'
      }
    ]
  }
];

// Helper to validate and build coordinate map for a grid
export interface CellData {
  row: number;
  col: number;
  expectedChar: string;
  char: string;
  number?: number;
  acrossWordId?: string;
  downWordId?: string;
  isSpace?: boolean;
}

/**
 * Returns all individual grid cells for a word or phrase, supporting
 * multi-line line breaks and dedicated space cells.
 */
export function getWordCells(w: CrosswordWord): CrosswordWordCell[] {
  if (w.cells && w.cells.length > 0) {
    return w.cells;
  }
  if ((w as any)._cachedCells) {
    return (w as any)._cachedCells;
  }
  const letters = w.kab.toUpperCase().split('');
  const cells: CrosswordWordCell[] = letters.map((char, idx) => ({
    row: w.direction === 'across' ? w.row : w.row + idx,
    col: w.direction === 'across' ? w.col + idx : w.col,
    char,
    isSpace: char === ' ',
  }));
  (w as any)._cachedCells = cells;
  return cells;
}

export function buildGridMatrix(
  grid: CrosswordGrid,
  visibleWordIds?: Set<string>
): {
  matrix: (CellData | null)[][];
  cellMap: Record<string, CellData>;
} {
  const matrix: (CellData | null)[][] = Array.from({ length: grid.rows }, () =>
    Array.from({ length: grid.cols }, () => null)
  );
  const cellMap: Record<string, CellData> = {};

  const wordsToInclude = visibleWordIds
    ? grid.words.filter((w) => visibleWordIds.has(w.id))
    : grid.words;

  wordsToInclude.forEach((w) => {
    const wordCells = getWordCells(w);
    wordCells.forEach((cell, idx) => {
      const r = cell.row;
      const c = cell.col;
      const char = cell.char;
      const key = `${r}_${c}`;

      if (r >= grid.rows || c >= grid.cols || r < 0 || c < 0) {
        console.error(`[Crossword] Word ${w.id} cell out of bounds at (${r},${c})`);
        return;
      }

      if (!matrix[r]) matrix[r] = [];

      const existing = matrix[r][c];
      if (existing) {
        if (!cell.isSpace && existing.expectedChar !== char) {
          console.warn(`[Crossword] Character mismatch in grid ${grid.id} at (${r},${c}): existing '${existing.expectedChar}' vs new '${char}'`);
        }
        if (idx === 0 && !cell.isSpace) existing.number = w.number;
        if (w.direction === 'across' || w.isPhrase) existing.acrossWordId = w.id;
        else existing.downWordId = w.id;
        if (cell.isSpace) existing.isSpace = true;
      } else {
        const cellData: CellData = {
          row: r,
          col: c,
          expectedChar: char,
          char,
          number: idx === 0 && !cell.isSpace ? w.number : undefined,
          acrossWordId: w.direction === 'across' || w.isPhrase ? w.id : undefined,
          downWordId: w.direction === 'down' && !w.isPhrase ? w.id : undefined,
          isSpace: cell.isSpace,
        };
        matrix[r][c] = cellData;
        cellMap[key] = cellData;
      }
    });
  });

  return { matrix, cellMap };
}

